// ─── FO-517 (spec 217) — gestructureerde sollicitatiebeoordeling ─────────────
//
// Eén bron voor API én backoffice: enums (gespiegeld uit Prisma als string-
// unions), labels, request-schema's (de API valideert er serverside mee, het
// formulier client-side vóór verzenden) en de DTO's van het kandidaatdossier.
// De beslisregels (scores, pro's/con's, systeemadvies, consistentie, salaris)
// staan als pure functies in `@streep/utils` (`recruitment-beoordeling.ts`).
import { z } from 'zod'
import { KANDIDAAT_NIVEAU, type KandidaatNiveau, type KandidaatStatus } from './kandidaten'
import { isBestaandeKalenderdag } from './recruitment-sollicitant'

// ─── Enums ──────────────────────────────────────────────────────────────────

export const VACATURE_STATUS = ['CONCEPT', 'OPEN', 'GESLOTEN'] as const
export type VacatureStatus = (typeof VACATURE_STATUS)[number]

export const FUNCTIE_EIS_CATEGORIE = [
  'TECHNISCH_NIVEAU', 'ERVARING', 'RIJBEWIJS', 'PLOEGEN', 'STORINGSDIENST', 'CERTIFICAAT',
  'NEDERLANDS', 'REISBEREIDHEID', 'SALARISBAND', 'BESCHIKBAARHEID', 'OVERIG',
] as const
export type FunctieEisCategorie = (typeof FUNCTIE_EIS_CATEGORIE)[number]

export const VACATURE_EIS_PRIORITEIT = ['MUST_HAVE', 'NICE_TO_HAVE', 'ONTWIKKELBAAR'] as const
export type VacatureEisPrioriteit = (typeof VACATURE_EIS_PRIORITEIT)[number]

export const EIS_TOETS_UITKOMST = ['VOLDOET', 'VOLDOET_NIET', 'ONBEKEND'] as const
export type EisToetsUitkomst = (typeof EIS_TOETS_UITKOMST)[number]

export const BEOORDELING_ONDERDEEL = ['PERSOON', 'TECHNISCH', 'ALGEMEEN', 'HISTORIE_TOEKOMST', 'ARBEIDSVOORWAARDEN'] as const
export type BeoordelingOnderdeel = (typeof BEOORDELING_ONDERDEEL)[number]

export const BEOORDELING_WEGINGSGROEP = [
  'TECHNISCH', 'WERKERVARING', 'PERSOON_FIT', 'ALGEMEEN', 'MOTIVATIE_TOEKOMST', 'OPLEIDINGEN',
] as const
export type BeoordelingWegingsgroep = (typeof BEOORDELING_WEGINGSGROEP)[number]

export const BEOORDELING_BRON = [
  // batch-70 (FO-530 §B3): TELEFONISCH_GESPREK direct na Interview in de keuzelijst.
  'INTERVIEW', 'TELEFONISCH_GESPREK', 'CV', 'WERKERVARING', 'DIPLOMA', 'CERTIFICAAT', 'PRAKTIJKVOORBEELD',
  'TECHNISCHE_TEST', 'REFERENTIE', 'NOTITIE', 'ONVOLDOENDE_INFORMATIE',
] as const
export type BeoordelingBron = (typeof BEOORDELING_BRON)[number]

export const BEOORDELING_ZEKERHEID = ['BEWEZEN', 'AANNEMELIJK', 'ALLEEN_KANDIDAAT', 'NOG_TE_CONTROLEREN'] as const
export type BeoordelingZekerheid = (typeof BEOORDELING_ZEKERHEID)[number]

export const BEOORDELING_SIGNAAL = ['POSITIEF', 'AANDACHTSPUNT', 'SERIEUS_RISICO', 'ONVOLDOENDE_INFORMATIE'] as const
export type BeoordelingSignaal = (typeof BEOORDELING_SIGNAAL)[number]

export const BEOORDELAAR_ROL = ['RECRUITER', 'HR', 'TL', 'DIRECTIE'] as const
export type BeoordelaarRol = (typeof BEOORDELAAR_ROL)[number]
/** De drie second-review-rollen (2 van 3). */
export const SECOND_REVIEW_ROLLEN = ['HR', 'TL', 'DIRECTIE'] as const satisfies readonly BeoordelaarRol[]

export const KANDIDAAT_BEOORDELING_STATUS = ['CONCEPT', 'AFGEROND', 'VERVANGEN'] as const
export type KandidaatBeoordelingStatus = (typeof KANDIDAAT_BEOORDELING_STATUS)[number]

export const TECHNISCH_OORDEEL = ['VOLDOENDE', 'ONVOLDOENDE'] as const
export type TechnischOordeel = (typeof TECHNISCH_OORDEEL)[number]

export const KANDIDAAT_ADVIES_KEUZE = ['AANNEMEN', 'TWEEDE_GESPREK', 'HERBEOORDELING', 'AANVULLENDE_INFO', 'AFWIJZEN'] as const
export type KandidaatAdviesKeuze = (typeof KANDIDAAT_ADVIES_KEUZE)[number]

export const KANDIDAAT_ADVIES_SOORT = ['RECRUITER', 'SYSTEEM', 'BEOORDELAAR', 'BESLUIT'] as const
export type KandidaatAdviesSoort = (typeof KANDIDAAT_ADVIES_SOORT)[number]

export const REVIEW_REACTIE = ['VOLGEN', 'AFWIJKEN', 'INFO_OPVRAGEN'] as const
export type ReviewReactie = (typeof REVIEW_REACTIE)[number]

export const RECRUITER_AFWIJKING_REACTIE = [
  'HANDHAVEN', 'AANPASSEN', 'EXTRA_ONDERBOUWING', 'INFO_TOEVOEGEN', 'OPNIEUW_SPREKEN', 'TECHNISCHE_VERIFICATIE',
] as const
export type RecruiterAfwijkingReactie = (typeof RECRUITER_AFWIJKING_REACTIE)[number]

export const INFO_VERZOEK_STATUS = ['OPEN', 'BEANTWOORD', 'VERVALLEN'] as const
export type InfoVerzoekStatus = (typeof INFO_VERZOEK_STATUS)[number]

export const VERIFICATIE_SOORT = ['VRAGENLIJST', 'PRAKTIJKTEST', 'MEELOOPMOMENT', 'SPECIALIST_GESPREK'] as const
export type VerificatieSoort = (typeof VERIFICATIE_SOORT)[number]

export const VERIFICATIE_STATUS = ['GEPLAND', 'UITGEVOERD', 'GEANNULEERD'] as const
export type VerificatieStatus = (typeof VERIFICATIE_STATUS)[number]

export const VERTREK_REDEN = [
  'ONTWIKKELING', 'SALARIS', 'REORGANISATIE', 'CONTRACT_BEEINDIGD', 'VERHUIZING', 'WERKTIJDEN',
  'MANAGEMENT', 'CONFLICT', 'WERKDRUK', 'CARRIERESWITCH', 'ANDERE_FUNCTIE', 'ONBEKEND', 'ANDERS',
] as const
export type VertrekReden = (typeof VERTREK_REDEN)[number]
/** Vertrekredenen die als patroonsignaal (risico) tellen; alleen feitelijk getoond. */
export const NEGATIEVE_VERTREKREDENEN: readonly VertrekReden[] = ['CONFLICT', 'MANAGEMENT', 'WERKDRUK']

export const VERTREK_TOON = ['POSITIEF', 'NEUTRAAL', 'NEGATIEF'] as const
export type VertrekToon = (typeof VERTREK_TOON)[number]

export const TAAL_NIVEAU = ['GEEN', 'BASIS', 'REDELIJK', 'GOED', 'VLOEIEND'] as const
export type TaalNiveau = (typeof TAAL_NIVEAU)[number]

export const ARBEIDSVOORWAARDE_SOORT = [
  'HUIDIG_SALARIS', 'WENS_SALARIS', 'ONDERGRENS_SALARIS', 'UREN', 'PLOEGEN', 'REISKOSTEN', 'AUTO',
  'TELEFOON', 'OPLEIDINGEN', 'PENSIOEN', 'VRIJE_DAGEN', 'STARTDATUM', 'OPZEGTERMIJN', 'OVERIG',
] as const
export type ArbeidsvoorwaardeSoort = (typeof ARBEIDSVOORWAARDE_SOORT)[number]

export const GESPREK_WIJZIGING_CATEGORIE = ['ZIEKTE', 'WERK', 'PRIVE', 'ONBEKEND'] as const
export type GesprekWijzigingCategorie = (typeof GESPREK_WIJZIGING_CATEGORIE)[number]

export const FUNCTIEMATRIX_STATUS = ['CONCEPT', 'ACTIEF', 'ARCHIEF'] as const
export type FunctiematrixStatus = (typeof FUNCTIEMATRIX_STATUS)[number]

export const SALARIS_FACTOR_SOORT = [
  'TECHNISCH_NIVEAU', 'ERVARING_JAREN', 'ZELFSTANDIGHEID', 'STORINGSVAARDIGHEID',
  'VERANTWOORDELIJKHEID', 'SPECIALISATIE', 'PLOEGEN', 'CERTIFICERING',
] as const
export type SalarisFactorSoort = (typeof SALARIS_FACTOR_SOORT)[number]

// batch-70 (FO-530 §D7, A3) — weergave van het formulier en de gespreksfase.
export const BEOORDELING_WEERGAVE_MODUS = ['GESPREK', 'UITWERK'] as const
export type BeoordelingWeergaveModus = (typeof BEOORDELING_WEERGAVE_MODUS)[number]

export const BEOORDELING_GESPREKSFASE = ['TELEFONISCH', 'INTERVIEW', 'TWEEDE_GESPREK'] as const
export type BeoordelingGespreksfase = (typeof BEOORDELING_GESPREKSFASE)[number]

// ─── Labels ─────────────────────────────────────────────────────────────────

export const VACATURE_STATUS_LABELS: Record<VacatureStatus, string> = {
  CONCEPT: 'Concept', OPEN: 'Open', GESLOTEN: 'Gesloten',
}
export const FUNCTIE_EIS_CATEGORIE_LABELS: Record<FunctieEisCategorie, string> = {
  TECHNISCH_NIVEAU: 'Technisch niveau', ERVARING: 'Ervaring', RIJBEWIJS: 'Rijbewijs', PLOEGEN: 'Ploegen',
  STORINGSDIENST: 'Storingsdienst', CERTIFICAAT: 'Certificaat', NEDERLANDS: 'Nederlands',
  REISBEREIDHEID: 'Reisbereidheid', SALARISBAND: 'Salarisband', BESCHIKBAARHEID: 'Beschikbaarheid', OVERIG: 'Overig',
}
export const VACATURE_EIS_PRIORITEIT_LABELS: Record<VacatureEisPrioriteit, string> = {
  MUST_HAVE: 'Must-have', NICE_TO_HAVE: 'Nice-to-have', ONTWIKKELBAAR: 'Ontwikkelbaar',
}
export const EIS_TOETS_UITKOMST_LABELS: Record<EisToetsUitkomst, string> = {
  VOLDOET: 'Voldoet', VOLDOET_NIET: 'Voldoet niet', ONBEKEND: 'Onbekend',
}
export const BEOORDELING_ONDERDEEL_LABELS: Record<BeoordelingOnderdeel, string> = {
  PERSOON: 'Persoon & persoonlijkheid', TECHNISCH: 'Technische vaardigheden', ALGEMEEN: 'Algemene vaardigheden',
  HISTORIE_TOEKOMST: 'Historie & toekomst', ARBEIDSVOORWAARDEN: 'Arbeidsvoorwaarden',
}
export const BEOORDELING_WEGINGSGROEP_LABELS: Record<BeoordelingWegingsgroep, string> = {
  TECHNISCH: 'Technisch', WERKERVARING: 'Werkervaring', PERSOON_FIT: 'Persoon/fit', ALGEMEEN: 'Algemeen',
  MOTIVATIE_TOEKOMST: 'Motivatie/toekomst', OPLEIDINGEN: 'Opleidingen',
}
export const BEOORDELING_BRON_LABELS: Record<BeoordelingBron, string> = {
  INTERVIEW: 'Interview', TELEFONISCH_GESPREK: 'Telefonisch gesprek', CV: 'CV', WERKERVARING: 'Werkervaring', DIPLOMA: 'Diploma', CERTIFICAAT: 'Certificaat',
  PRAKTIJKVOORBEELD: 'Praktijkvoorbeeld', TECHNISCHE_TEST: 'Technische test', REFERENTIE: 'Referentie',
  NOTITIE: 'Notitie', ONVOLDOENDE_INFORMATIE: 'Onvoldoende informatie',
}
export const BEOORDELING_ZEKERHEID_LABELS: Record<BeoordelingZekerheid, string> = {
  BEWEZEN: 'Bewezen', AANNEMELIJK: 'Aannemelijk', ALLEEN_KANDIDAAT: 'Alleen door kandidaat aangegeven',
  NOG_TE_CONTROLEREN: 'Nog te controleren',
}
export const BEOORDELING_SIGNAAL_LABELS: Record<BeoordelingSignaal, string> = {
  POSITIEF: 'Positief', AANDACHTSPUNT: 'Aandachtspunt', SERIEUS_RISICO: 'Serieus risico',
  ONVOLDOENDE_INFORMATIE: 'Onvoldoende informatie',
}
export const BEOORDELAAR_ROL_LABELS: Record<BeoordelaarRol, string> = {
  RECRUITER: 'Recruiter', HR: 'HR-beoordelaar', TL: 'TL-beoordelaar', DIRECTIE: 'Directie',
}
export const KANDIDAAT_ADVIES_KEUZE_LABELS: Record<KandidaatAdviesKeuze, string> = {
  AANNEMEN: 'Aannemen', TWEEDE_GESPREK: 'Tweede gesprek', HERBEOORDELING: 'Herbeoordeling',
  AANVULLENDE_INFO: 'Aanvullende info', AFWIJZEN: 'Afwijzen',
}
export const REVIEW_REACTIE_LABELS: Record<ReviewReactie, string> = {
  VOLGEN: 'Advies volgen', AFWIJKEN: 'Afwijken', INFO_OPVRAGEN: 'Aanvullende info opvragen',
}
export const RECRUITER_AFWIJKING_REACTIE_LABELS: Record<RecruiterAfwijkingReactie, string> = {
  HANDHAVEN: 'Advies handhaven', AANPASSEN: 'Advies aanpassen', EXTRA_ONDERBOUWING: 'Extra onderbouwing',
  INFO_TOEVOEGEN: 'Informatie toevoegen', OPNIEUW_SPREKEN: 'Kandidaat opnieuw spreken',
  TECHNISCHE_VERIFICATIE: 'Technische verificatie',
}
export const INFO_VERZOEK_STATUS_LABELS: Record<InfoVerzoekStatus, string> = {
  OPEN: 'Open', BEANTWOORD: 'Beantwoord', VERVALLEN: 'Vervallen',
}
export const VERIFICATIE_SOORT_LABELS: Record<VerificatieSoort, string> = {
  VRAGENLIJST: 'Vragenlijst', PRAKTIJKTEST: 'Praktijktest', MEELOOPMOMENT: 'Meeloopmoment',
  SPECIALIST_GESPREK: 'Gesprek met specialist',
}
export const VERIFICATIE_STATUS_LABELS: Record<VerificatieStatus, string> = {
  GEPLAND: 'Gepland', UITGEVOERD: 'Uitgevoerd', GEANNULEERD: 'Geannuleerd',
}
export const VERTREK_REDEN_LABELS: Record<VertrekReden, string> = {
  ONTWIKKELING: 'Ontwikkeling', SALARIS: 'Salaris', REORGANISATIE: 'Reorganisatie',
  CONTRACT_BEEINDIGD: 'Contract beëindigd', VERHUIZING: 'Verhuizing', WERKTIJDEN: 'Werktijden',
  MANAGEMENT: 'Management', CONFLICT: 'Conflict', WERKDRUK: 'Werkdruk', CARRIERESWITCH: 'Carrièreswitch',
  ANDERE_FUNCTIE: 'Andere functie', ONBEKEND: 'Onbekend', ANDERS: 'Anders',
}
export const VERTREK_TOON_LABELS: Record<VertrekToon, string> = {
  POSITIEF: 'Positief', NEUTRAAL: 'Neutraal', NEGATIEF: 'Negatief',
}
export const TAAL_NIVEAU_LABELS: Record<TaalNiveau, string> = {
  GEEN: 'Geen', BASIS: 'Basis', REDELIJK: 'Redelijk', GOED: 'Goed', VLOEIEND: 'Vloeiend',
}
export const ARBEIDSVOORWAARDE_SOORT_LABELS: Record<ArbeidsvoorwaardeSoort, string> = {
  HUIDIG_SALARIS: 'Huidig salaris', WENS_SALARIS: 'Salariswens', ONDERGRENS_SALARIS: 'Ondergrens salaris',
  UREN: 'Uren', PLOEGEN: 'Ploegen', REISKOSTEN: 'Reiskosten', AUTO: 'Auto', TELEFOON: 'Telefoon',
  OPLEIDINGEN: 'Opleidingen', PENSIOEN: 'Pensioen', VRIJE_DAGEN: 'Vrije dagen', STARTDATUM: 'Startdatum',
  OPZEGTERMIJN: 'Opzegtermijn', OVERIG: 'Overig',
}
export const GESPREK_WIJZIGING_CATEGORIE_LABELS: Record<GesprekWijzigingCategorie, string> = {
  ZIEKTE: 'Ziekte', WERK: 'Werk', PRIVE: 'Privé', ONBEKEND: 'Onbekend',
}
export const FUNCTIEMATRIX_STATUS_LABELS: Record<FunctiematrixStatus, string> = {
  CONCEPT: 'Concept', ACTIEF: 'Actief', ARCHIEF: 'Archief',
}
export const SALARIS_FACTOR_SOORT_LABELS: Record<SalarisFactorSoort, string> = {
  TECHNISCH_NIVEAU: 'Technisch niveau (score)', ERVARING_JAREN: 'Relevante ervaring (jaren)',
  ZELFSTANDIGHEID: 'Zelfstandigheid (score)', STORINGSVAARDIGHEID: 'Storingsvaardigheid (score)',
  VERANTWOORDELIJKHEID: 'Verantwoordelijkheid (score)', SPECIALISATIE: 'Specialisaties (aantal)',
  PLOEGEN: 'Ploegeninzetbaarheid (1 = ja)', CERTIFICERING: 'Relevante certificeringen (aantal)',
}

export const BEOORDELING_WEERGAVE_MODUS_LABELS: Record<BeoordelingWeergaveModus, string> = {
  GESPREK: 'Gespreksmodus', UITWERK: 'Uitwerkmodus',
}
export const BEOORDELING_GESPREKSFASE_LABELS: Record<BeoordelingGespreksfase, string> = {
  TELEFONISCH: 'Telefonisch', INTERVIEW: 'Interview', TWEEDE_GESPREK: 'Tweede gesprek',
}
/** A3 — de fase bepaalt de standaardbron van een score (zichtbaar voorgevuld). */
export const GESPREKSFASE_STANDAARD_BRON: Record<BeoordelingGespreksfase, BeoordelingBron> = {
  TELEFONISCH: 'TELEFONISCH_GESPREK', INTERVIEW: 'INTERVIEW', TWEEDE_GESPREK: 'INTERVIEW',
}
/** A3 — zonder gekozen fase staat het formulier op Interview. */
export const STANDAARD_GESPREKSFASE: BeoordelingGespreksfase = 'INTERVIEW'

/** Kortere sectielabels voor de sectienavigatie van het formulier (§D6). */
export const BEOORDELING_ONDERDEEL_KORT_LABELS: Record<BeoordelingOnderdeel, string> = {
  PERSOON: 'Persoon', TECHNISCH: 'Technisch', ALGEMEEN: 'Algemeen',
  HISTORIE_TOEKOMST: 'Historie', ARBEIDSVOORWAARDEN: 'Arbeidsvoorwaarden',
}

/** A4 — minimumlengte van een onderbouwing of onderdeelnotitie (de UI toont de teller). */
export const ONDERBOUWING_MIN_TEKENS = 3

/** Motivatielijst (§C4, primair/secundair). */
export const MOTIVATIE_OPTIES = [
  'Salaris', 'Ontwikkeling', 'Uitdaging', 'Doorgroei', 'Verantwoordelijkheid', 'Zekerheid', 'Sfeer',
  'Flexibiliteit', 'Werktijden', 'Afstand', 'Anders',
] as const

/** §E10 — verplichte reden als technisch sterk (>= 4) toch "onvoldoende" is. */
export const TECHNISCH_ONVOLDOENDE_REDENEN = [
  'Ervaring niet relevant voor onze installaties',
  'Basisvragen niet beantwoord',
  'Stelt kennis sterker voor dan de voorbeelden tonen',
  'Ervaring in montage in plaats van storing',
  'Anders',
] as const

/** Standaard eigen onderdelen per rol (§G14); per beoordeling aanpasbaar. */
export const ROL_STANDAARD_ONDERDELEN: Record<BeoordelaarRol, readonly BeoordelingOnderdeel[]> = {
  RECRUITER: BEOORDELING_ONDERDEEL,
  HR: ['PERSOON', 'ALGEMEEN', 'HISTORIE_TOEKOMST', 'ARBEIDSVOORWAARDEN'],
  TL: ['TECHNISCH', 'ALGEMEEN', 'HISTORIE_TOEKOMST'],
  DIRECTIE: BEOORDELING_ONDERDEEL,
}

/** §F11 — seed-weging (storingsmonteur). Som = 100. */
export const STANDAARD_WEGINGEN: Record<BeoordelingWegingsgroep, number> = {
  TECHNISCH: 40, WERKERVARING: 20, PERSOON_FIT: 15, ALGEMEEN: 10, MOTIVATIE_TOEKOMST: 10, OPLEIDINGEN: 5,
}

/** Verschil tussen beoordelaars (zelfde groep) waarboven bespreking vereist is. */
export const GROTE_AFWIJKING_DREMPEL = 1.5

// ─── Request-schema's ───────────────────────────────────────────────────────

const id = z.string().trim().min(1).max(60)
const tekst = (max: number) => z.string().trim().max(max)
const optTekst = (max: number) => z.string().trim().max(max).nullable().optional()
// batch-70 (FO-530): een echte kalenderdag (zelfde regel als de certificaat-vinkjes).
const datum = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum (jjjj-mm-dd)').refine(isBestaandeKalenderdag, 'Deze datum bestaat niet')
const bedrag = z.number().min(0).max(1_000_000)

export const wegingenSchema = z
  .object({
    TECHNISCH: z.number().int().min(0).max(100),
    WERKERVARING: z.number().int().min(0).max(100),
    PERSOON_FIT: z.number().int().min(0).max(100),
    ALGEMEEN: z.number().int().min(0).max(100),
    MOTIVATIE_TOEKOMST: z.number().int().min(0).max(100),
    OPLEIDINGEN: z.number().int().min(0).max(100),
  })
  .superRefine((w, ctx) => {
    const som = Object.values(w).reduce((a, b) => a + b, 0)
    if (som !== 100) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['TECHNISCH'], message: `De wegingen tellen op tot ${som}%, dat moet 100% zijn` })
  })
export type Wegingen = z.infer<typeof wegingenSchema>

export const vacatureSchema = z.object({
  titel: tekst(200).min(1, 'Titel is verplicht'),
  profielFunctie: z.enum(['HTD', 'MANAGEMENT', 'STORINGSMONTEUR', 'PROJECTMANAGER', 'SOFTWARE', 'TEKENAAR']).nullable().optional(),
  entityId: id.nullable().optional(),
  niveau: z.enum(KANDIDAAT_NIVEAU).nullable().optional(),
  status: z.enum(VACATURE_STATUS).optional(),
  recruiterId: id.nullable().optional(),
  wegingen: wegingenSchema.optional(),
  technischeSkillIds: z.array(id).max(60).optional(),
  functiegroep: optTekst(100),
  omschrijving: optTekst(5000),
})
export type VacatureInput = z.infer<typeof vacatureSchema>

export const functieEisSchema = z.object({
  naam: tekst(200).min(1, 'Naam is verplicht'),
  categorie: z.enum(FUNCTIE_EIS_CATEGORIE),
  omschrijving: optTekst(1000),
  actief: z.boolean().optional(),
})
export type FunctieEisInput = z.infer<typeof functieEisSchema>

export const vacatureEisSchema = z.object({
  functieEisId: id,
  prioriteit: z.enum(VACATURE_EIS_PRIORITEIT),
  toelichting: optTekst(500),
})
export type VacatureEisInput = z.infer<typeof vacatureEisSchema>

export const eisToetsingSchema = z.object({
  vacatureEisId: id,
  uitkomst: z.enum(EIS_TOETS_UITKOMST),
  bron: z.enum(BEOORDELING_BRON).nullable().optional(),
  toelichting: optTekst(1000),
}).superRefine((v, ctx) => {
  if (v.uitkomst !== 'ONBEKEND' && !v.bron) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bron'], message: 'Geef de bron waaruit dit blijkt' })
})
export type EisToetsingInput = z.infer<typeof eisToetsingSchema>

export const criteriumSchema = z.object({
  sleutel: tekst(80).min(1).regex(/^[a-z0-9.-]+$/, 'Alleen kleine letters, cijfers, punt en streepje'),
  naam: tekst(200).min(1, 'Naam is verplicht'),
  omschrijving: optTekst(1000),
  onderdeel: z.enum(BEOORDELING_ONDERDEEL),
  wegingsgroep: z.enum(BEOORDELING_WEGINGSGROEP),
  volgorde: z.number().int().min(0).max(1000).optional(),
  actief: z.boolean().optional(),
})
export type CriteriumInput = z.infer<typeof criteriumSchema>

/**
 * §C4-5 — één beoordelingsregel. Score 1-5 of `null` (= N/B, telt nooit mee).
 *
 * Het formulier wordt tijdens het gesprek ingevuld: een concept mag dus
 * onvolledig zijn (alleen een score is genoeg). De inhoudsregels (onderbouwing
 * en bron bij een score, geen lage score op "onvoldoende informatie",
 * aandachtspunt/risico onderbouwd) gelden pas bij afronden, zie
 * `valideerBeoordelingAfronden` in `@streep/utils`. Zekerheid is optioneel.
 */
export const beoordelingRegelSchema = z
  .object({
    criteriumId: id.nullable().optional(),
    skillId: id.nullable().optional(),
    score: z.number().int().min(1).max(5).nullable(),
    onderbouwing: optTekst(4000),
    bron: z.enum(BEOORDELING_BRON).nullable().optional(),
    zekerheid: z.enum(BEOORDELING_ZEKERHEID).nullable().optional(),
    signaal: z.enum(BEOORDELING_SIGNAAL).nullable().optional(),
  })
  .superRefine((r, ctx) => {
    if (!r.criteriumId === !r.skillId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['criteriumId'], message: 'Kies een criterium of een vakgebied' })
    }
  })
export type BeoordelingRegelInput = z.infer<typeof beoordelingRegelSchema>

export const beoordelingStartSchema = z.object({
  rol: z.enum(BEOORDELAAR_ROL),
  ronde: z.number().int().min(1).max(10).optional(),
  // batch-70 (FO-530 §E10): expliciete fase (bv. vanuit een gesprek); leeg = de
  // server leidt hem af uit de status en het laatste gesprek.
  fase: z.enum(BEOORDELING_GESPREKSFASE).optional(),
})
export type BeoordelingStartInput = z.infer<typeof beoordelingStartSchema>

/**
 * batch-70 (FO-530 §D7) — één gespreksnotitie per onderdeel. Onbekende sleutels
 * vallen weg (de read-DTO levert alleen de vijf onderdelen terug).
 */
export const onderdeelNotitiesSchema = z.object({
  PERSOON: tekst(8000).optional(),
  TECHNISCH: tekst(8000).optional(),
  ALGEMEEN: tekst(8000).optional(),
  HISTORIE_TOEKOMST: tekst(8000).optional(),
  ARBEIDSVOORWAARDEN: tekst(8000).optional(),
})
export type OnderdeelNotities = Partial<Record<BeoordelingOnderdeel, string>>

/** Leest de Json-kolom `onderdeelNotities` defensief (alleen bekende onderdelen, alleen tekst). */
export function leesOnderdeelNotities(raw: unknown): OnderdeelNotities {
  const uit: OnderdeelNotities = {}
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return uit
  for (const o of BEOORDELING_ONDERDEEL) {
    const v = (raw as Record<string, unknown>)[o]
    if (typeof v === 'string' && v.trim() !== '') uit[o] = v
  }
  return uit
}

export const beoordelingOpslaanSchema = z.object({
  onderdelen: z.array(z.enum(BEOORDELING_ONDERDEEL)).min(1).optional(),
  regels: z.array(beoordelingRegelSchema).max(200),
  motivatiePrimair: optTekst(80),
  motivatieSecundair: optTekst(80),
  motivatieToelichting: optTekst(4000),
  ontwikkelwens: optTekst(4000),
  verwachting1Jaar: optTekst(2000),
  verwachting3Jaar: optTekst(2000),
  verwachting5Jaar: optTekst(2000),
  technischOordeel: z.enum(TECHNISCH_OORDEEL).nullable().optional(),
  technischOordeelReden: optTekst(200),
  technischOordeelToelichting: optTekst(4000),
  jobhopperOordeel: z.boolean().optional(),
  jobhopperOnderbouwing: optTekst(4000),
  technischSterkIn: optTekst(2000),
  technischOntwikkeling: optTekst(2000),
  // batch-70 (FO-530 §D) — weergave, gespreksnotities per onderdeel en fase.
  weergaveModus: z.enum(BEOORDELING_WEERGAVE_MODUS).nullable().optional(),
  onderdeelNotities: onderdeelNotitiesSchema.optional(),
  gespreksfase: z.enum(BEOORDELING_GESPREKSFASE).nullable().optional(),
})
export type BeoordelingOpslaanInput = z.infer<typeof beoordelingOpslaanSchema>

export const recruiterAdviesSchema = z.object({
  keuze: z.enum(KANDIDAAT_ADVIES_KEUZE),
  onderbouwing: tekst(4000).min(10, 'Onderbouw het advies (minimaal 10 tekens)'),
})
export type RecruiterAdviesInput = z.infer<typeof recruiterAdviesSchema>

export const reviewReactieSchema = z.discriminatedUnion('reactie', [
  z.object({
    reactie: z.literal('VOLGEN'),
    onderbouwing: tekst(4000).optional(),
  }),
  z.object({
    reactie: z.literal('AFWIJKEN'),
    afwijkingPunten: tekst(4000).min(3, 'Waarmee ben je het oneens?'),
    afwijkingInfo: tekst(4000).min(3, 'Welke informatie ontbreekt of lees je anders?'),
    alternatiefAdvies: z.enum(KANDIDAAT_ADVIES_KEUZE),
    onderbouwing: tekst(4000).min(10, 'Onderbouw je alternatieve advies'),
  }),
  z.object({
    reactie: z.literal('INFO_OPVRAGEN'),
    omschrijving: tekst(4000).min(3, 'Welke informatie is nodig?'),
    gevraagdVanId: id.nullable().optional(),
    gevraagdVanTekst: optTekst(200),
    deadline: datum,
  }),
])
export type ReviewReactieInput = z.infer<typeof reviewReactieSchema>

export const recruiterAfwijkingReactieSchema = z
  .object({
    afwijkingAdviesId: id,
    reactie: z.enum(RECRUITER_AFWIJKING_REACTIE),
    onderbouwing: tekst(4000).min(10, 'Licht je reactie toe'),
    nieuweKeuze: z.enum(KANDIDAAT_ADVIES_KEUZE).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.reactie === 'AANPASSEN' && !v.nieuweKeuze) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['nieuweKeuze'], message: 'Kies het aangepaste advies' })
  })
export type RecruiterAfwijkingReactieInput = z.infer<typeof recruiterAfwijkingReactieSchema>

export const infoVerzoekSchema = z.object({
  omschrijving: tekst(4000).min(3, 'Welke informatie is nodig?'),
  gevraagdVanId: id.nullable().optional(),
  gevraagdVanTekst: optTekst(200),
  deadline: datum,
})
export type InfoVerzoekInput = z.infer<typeof infoVerzoekSchema>

export const infoVerzoekAntwoordSchema = z.object({
  antwoord: tekst(8000).min(3, 'Geef het antwoord'),
})
export type InfoVerzoekAntwoordInput = z.infer<typeof infoVerzoekAntwoordSchema>

export const verificatieSchema = z.object({
  soort: z.enum(VERIFICATIE_SOORT),
  uitvoerderId: id.nullable().optional(),
  datum: datum.nullable().optional(),
})
export type VerificatieInput = z.infer<typeof verificatieSchema>

/** §G16 — uitkomst: regels krijgen serverside bron TECHNISCHE_TEST + BEWEZEN. */
export const verificatieUitkomstSchema = z.object({
  uitkomst: tekst(8000).min(3, 'Beschrijf de uitkomst'),
  regels: z
    .array(z.object({
      skillId: id,
      score: z.number().int().min(1).max(5),
      onderbouwing: tekst(4000).min(3, 'Onderbouwing is verplicht'),
    }))
    .max(60),
})
export type VerificatieUitkomstInput = z.infer<typeof verificatieUitkomstSchema>

export const referentieSchema = z.object({
  toestemming: z.boolean(),
  naam: tekst(200).min(1, 'Naam is verplicht'),
  organisatie: optTekst(200),
  telefoon: optTekst(40),
  benaderdOp: datum.nullable().optional(),
  functieBevestigd: z.boolean().nullable().optional(),
  periodeBevestigd: z.boolean().nullable().optional(),
  werkzaamhedenBevestigd: z.boolean().nullable().optional(),
  terugkoppeling: optTekst(8000),
}).superRefine((v, ctx) => {
  if (v.benaderdOp && !v.toestemming) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['toestemming'], message: 'Zonder toestemming van de kandidaat mag de referent niet benaderd worden' })
})
export type ReferentieInput = z.infer<typeof referentieSchema>

export const besprekingSchema = z.object({
  wegingsgroep: z.enum(BEOORDELING_WEGINGSGROEP).nullable().optional(),
  notitie: tekst(8000).min(10, 'Leg vast wat er besproken en besloten is'),
})
export type BesprekingInput = z.infer<typeof besprekingSchema>

export const besluitSchema = z.object({
  keuze: z.enum(KANDIDAAT_ADVIES_KEUZE),
  onderbouwing: tekst(4000).min(10, 'Onderbouw het besluit'),
})
export type BesluitInput = z.infer<typeof besluitSchema>

export const reviewUitzonderingSchema = z.object({
  reden: tekst(1000).min(10, 'Geef de reden (bijvoorbeeld vakantie van een beoordelaar)'),
})
export type ReviewUitzonderingInput = z.infer<typeof reviewUitzonderingSchema>

export const kandidaatIntrekkenSchema = z.object({
  reden: tekst(1000).min(3, 'Geef de reden'),
})
export type KandidaatIntrekkenInput = z.infer<typeof kandidaatIntrekkenSchema>

export const werkgeverSchema = z.object({
  werkgever: tekst(200).min(1, 'Werkgever is verplicht'),
  functie: optTekst(200),
  van: datum.nullable().optional(),
  tot: datum.nullable().optional(),
  werkzaamheden: optTekst(4000),
  technischNiveau: z.number().int().min(1).max(5).nullable().optional(),
  vertrekReden: z.enum(VERTREK_REDEN).nullable().optional(),
  vertrekRedenAnders: optTekst(200),
  vertrekToon: z.enum(VERTREK_TOON).nullable().optional(),
  bijzonderheden: optTekst(2000),
  bevestigd: z.boolean().optional(),
}).superRefine((v, ctx) => {
  if (v.van && v.tot && v.tot < v.van) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tot'], message: 'Einddatum ligt voor de begindatum' })
  if (v.vertrekReden === 'ANDERS' && !v.vertrekRedenAnders) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vertrekRedenAnders'], message: 'Omschrijf de reden' })
})
export type WerkgeverInput = z.infer<typeof werkgeverSchema>

export const kandidaatOpleidingSchema = z.object({
  naam: tekst(200).min(1, 'Naam is verplicht'),
  niveau: optTekst(80),
  jaar: z.number().int().min(1950).max(2100).nullable().optional(),
  afgerond: z.boolean().nullable().optional(),
  redenNietAfgerond: optTekst(500),
  documentId: id.nullable().optional(),
  certificateTypeId: id.nullable().optional(),
  geldigTot: datum.nullable().optional(),
  gewenst: z.boolean().optional(),
  bevestigd: z.boolean().optional(),
})
export type KandidaatOpleidingInput = z.infer<typeof kandidaatOpleidingSchema>

export const kandidaatTaalSchema = z.object({
  taal: tekst(60).min(1, 'Taal is verplicht'),
  niveau: z.enum(TAAL_NIVEAU),
  spreken: z.enum(TAAL_NIVEAU).nullable().optional(),
  verstaan: z.enum(TAAL_NIVEAU).nullable().optional(),
  lezen: z.enum(TAAL_NIVEAU).nullable().optional(),
  schrijven: z.enum(TAAL_NIVEAU).nullable().optional(),
})
export type KandidaatTaalInput = z.infer<typeof kandidaatTaalSchema>

export const arbeidsvoorwaardeSchema = z.object({
  soort: z.enum(ARBEIDSVOORWAARDE_SOORT),
  waarde: optTekst(500),
  bedrag: bedrag.nullable().optional(),
  hardeEis: z.boolean(),
})
export type ArbeidsvoorwaardeInput = z.infer<typeof arbeidsvoorwaardeSchema>

/** Kandidaat-velden die de beoordeling aanvullen (§A2). */
export const kandidaatBeoordelingProfielSchema = z.object({
  vacatureId: id.nullable().optional(),
  motivatie: optTekst(8000),
  huidigeWerkgever: optTekst(200),
  opzegtermijn: optTekst(100),
  salarisWens: bedrag.nullable().optional(),
})
export type KandidaatBeoordelingProfielInput = z.infer<typeof kandidaatBeoordelingProfielSchema>

export const gesprekVerplaatsenSchema = z.object({
  nieuweDatum: z.string().datetime({ offset: true }),
  categorie: z.enum(GESPREK_WIJZIGING_CATEGORIE),
  reden: optTekst(500),
  doorKandidaat: z.boolean(),
})
export type GesprekVerplaatsenInput = z.infer<typeof gesprekVerplaatsenSchema>

export const gesprekBevestigingSchema = z.object({
  interviewerIds: z.array(id).max(10).optional(),
  bevestigingVerzonden: z.boolean().optional(),
  bevestigdDoorKandidaat: z.boolean().optional(),
})
export type GesprekBevestigingInput = z.infer<typeof gesprekBevestigingSchema>

export const functiematrixSchema = z.object({
  versie: tekst(40).min(1, 'Versie is verplicht'),
  ingangsdatum: datum,
  geldigTot: datum.nullable().optional(),
  toelichting: optTekst(4000),
  kopieVanId: id.nullable().optional(),
})
export type FunctiematrixInput = z.infer<typeof functiematrixSchema>

export const functiematrixStatusSchema = z.object({ status: z.enum(FUNCTIEMATRIX_STATUS) })

export const salarisschaalSchema = z
  .object({
    functiegroep: tekst(100).min(1, 'Functiegroep is verplicht'),
    niveau: z.number().int().min(1).max(10),
    min: bedrag,
    max: bedrag,
    adviesStart: bedrag,
  })
  .superRefine((v, ctx) => {
    if (v.max < v.min) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['max'], message: 'Maximum ligt onder het minimum' })
    if (v.adviesStart < v.min || v.adviesStart > v.max) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['adviesStart'], message: 'Advies-start moet binnen de schaal liggen' })
  })
export type SalarisschaalInput = z.infer<typeof salarisschaalSchema>

export const salarisFactorSchema = z.object({
  soort: z.enum(SALARIS_FACTOR_SOORT),
  label: tekst(200).min(1, 'Omschrijving is verplicht'),
  vanafWaarde: z.number().min(0).max(100),
  opslag: z.number().min(-100_000).max(100_000),
})
export type SalarisFactorInput = z.infer<typeof salarisFactorSchema>

export const salarisIndicatieBerekenSchema = z.object({
  functiegroep: optTekst(100),
  niveau: z.number().int().min(1).max(10).nullable().optional(),
})
export type SalarisIndicatieBerekenInput = z.infer<typeof salarisIndicatieBerekenSchema>

export const aanbiedingBandGoedkeuringSchema = z.object({
  bandOnderbouwing: tekst(4000).min(10, 'Onderbouw waarom buiten de band wordt aangeboden'),
})
export type AanbiedingBandGoedkeuringInput = z.infer<typeof aanbiedingBandGoedkeuringSchema>

// ─── DTO's ──────────────────────────────────────────────────────────────────

export interface VacatureEisDTO {
  id: string
  functieEisId: string
  naam: string
  categorie: FunctieEisCategorie
  prioriteit: VacatureEisPrioriteit
  toelichting: string | null
}

export interface VacatureDTO {
  id: string
  titel: string
  profielFunctie: string | null
  entityId: string | null
  niveau: KandidaatNiveau | null
  status: VacatureStatus
  recruiterId: string | null
  wegingen: Wegingen
  technischeSkillIds: string[]
  functiegroep: string | null
  omschrijving: string | null
  eisen: VacatureEisDTO[]
  aantalKandidaten: number
  createdAt: string
}

export interface FunctieEisDTO {
  id: string
  naam: string
  categorie: FunctieEisCategorie
  omschrijving: string | null
  actief: boolean
}

export interface CriteriumDTO {
  id: string
  sleutel: string
  naam: string
  omschrijving: string | null
  onderdeel: BeoordelingOnderdeel
  wegingsgroep: BeoordelingWegingsgroep
  volgorde: number
  actief: boolean
}

export interface EisToetsingDTO {
  vacatureEisId: string
  uitkomst: EisToetsUitkomst
  bron: BeoordelingBron | null
  toelichting: string | null
}

export interface BeoordelingRegelDTO {
  id: string
  criteriumId: string | null
  skillId: string | null
  /** Naam van criterium of vakgebied (voor weergave en herleiding). */
  naam: string
  onderdeel: BeoordelingOnderdeel
  wegingsgroep: BeoordelingWegingsgroep
  score: number | null
  onderbouwing: string | null
  bron: BeoordelingBron | null
  zekerheid: BeoordelingZekerheid | null
  signaal: BeoordelingSignaal | null
}

export interface BeoordelingDTO {
  id: string
  beoordelaarId: string
  beoordelaarNaam: string | null
  rol: BeoordelaarRol
  ronde: number
  versie: number
  status: KandidaatBeoordelingStatus
  onderdelen: BeoordelingOnderdeel[]
  afgerondOp: string | null
  motivatiePrimair: string | null
  motivatieSecundair: string | null
  motivatieToelichting: string | null
  ontwikkelwens: string | null
  verwachting1Jaar: string | null
  verwachting3Jaar: string | null
  verwachting5Jaar: string | null
  technischOordeel: TechnischOordeel | null
  technischOordeelReden: string | null
  technischOordeelToelichting: string | null
  jobhopperOordeel: boolean
  jobhopperOnderbouwing: string | null
  technischSterkIn: string | null
  technischOntwikkeling: string | null
  regels: BeoordelingRegelDTO[]
  updatedAt: string
  // batch-70 (FO-530 §D) — null = de standaard (zie `standaardWeergaveModus` / `STANDAARD_GESPREKSFASE`).
  weergaveModus: BeoordelingWeergaveModus | null
  onderdeelNotities: OnderdeelNotities
  gespreksfase: BeoordelingGespreksfase | null
}

export interface WerkgeverDTO {
  id: string
  werkgever: string
  functie: string | null
  van: string | null
  tot: string | null
  werkzaamheden: string | null
  technischNiveau: number | null
  vertrekReden: VertrekReden | null
  vertrekRedenAnders: string | null
  vertrekToon: VertrekToon | null
  bijzonderheden: string | null
  bevestigd: boolean
}

export interface KandidaatOpleidingDTO {
  id: string
  naam: string
  niveau: string | null
  jaar: number | null
  afgerond: boolean | null
  redenNietAfgerond: string | null
  documentId: string | null
  certificateTypeId: string | null
  certificateTypeNaam: string | null
  geldigTot: string | null
  gewenst: boolean
  bevestigd: boolean
}

export interface KandidaatTaalDTO {
  id: string
  taal: string
  niveau: TaalNiveau
  spreken: TaalNiveau | null
  verstaan: TaalNiveau | null
  lezen: TaalNiveau | null
  schrijven: TaalNiveau | null
}

export interface ArbeidsvoorwaardeDTO {
  id: string
  soort: ArbeidsvoorwaardeSoort
  waarde: string | null
  bedrag: string | null
  hardeEis: boolean
}

export interface AdviesDTO {
  id: string
  soort: KandidaatAdviesSoort
  auteurId: string | null
  auteurNaam: string | null
  rol: BeoordelaarRol | null
  ronde: number
  versie: number
  keuze: KandidaatAdviesKeuze | null
  reactie: ReviewReactie | null
  onderbouwing: string
  afwijkingPunten: string | null
  afwijkingInfo: string | null
  alternatiefAdvies: KandidaatAdviesKeuze | null
  recruiterReactie: RecruiterAfwijkingReactie | null
  reageertOpId: string | null
  vervangen: boolean
  createdAt: string
}

export interface InfoVerzoekDTO {
  id: string
  aangevraagdDoorNaam: string | null
  gevraagdVanId: string | null
  gevraagdVanNaam: string | null
  gevraagdVanTekst: string | null
  omschrijving: string
  deadline: string
  status: InfoVerzoekStatus
  antwoord: string | null
  beantwoordOp: string | null
}

export interface VerificatieDTO {
  id: string
  soort: VerificatieSoort
  status: VerificatieStatus
  uitvoerderId: string | null
  uitvoerderNaam: string | null
  datum: string | null
  uitkomst: string | null
}

export interface ReferentieDTO {
  id: string
  toestemming: boolean
  naam: string
  organisatie: string | null
  telefoon: string | null
  benaderdOp: string | null
  functieBevestigd: boolean | null
  periodeBevestigd: boolean | null
  werkzaamhedenBevestigd: boolean | null
  terugkoppeling: string | null
}

export interface BesprekingDTO {
  id: string
  wegingsgroep: BeoordelingWegingsgroep | null
  notitie: string
  auteurNaam: string | null
  createdAt: string
}

export interface SalarisIndicatieFactorDTO {
  soort: SalarisFactorSoort
  label: string
  waarde: number
  opslag: string
}

export interface SalarisIndicatieDTO {
  id: string
  matrixId: string
  matrixVersie: string
  matrixIngangsdatum: string
  functiegroep: string
  niveau: number
  schaalMin: string
  schaalMax: string
  adviesStart: string
  factoren: SalarisIndicatieFactorDTO[]
  onderbouwing: string
  wens: string | null
  verschil: string | null
  createdAt: string
  /** De matrixversie van berekening is niet meer de actieve versie (§5.5). */
  matrixVerouderd: boolean
}

export interface SalarisschaalDTO {
  id: string
  functiegroep: string
  niveau: number
  min: string
  max: string
  adviesStart: string
}

export interface SalarisFactorDTO {
  id: string
  soort: SalarisFactorSoort
  label: string
  vanafWaarde: string
  opslag: string
}

export interface FunctiematrixDTO {
  id: string
  versie: string
  ingangsdatum: string
  geldigTot: string | null
  status: FunctiematrixStatus
  toelichting: string | null
  schalen: SalarisschaalDTO[]
  factoren: SalarisFactorDTO[]
  createdAt: string
}

export interface GesprekWijzigingDTO {
  id: string
  categorie: GesprekWijzigingCategorie
  reden: string | null
  doorKandidaat: boolean
  oudeDatum: string
  nieuweDatum: string
  createdAt: string
}

export interface TijdlijnStapDTO {
  sleutel: string
  label: string
  datum: string | null
  status: 'GEDAAN' | 'OPEN' | 'TE_LAAT' | 'NVT'
  verantwoordelijke: string | null
  deadline: string | null
  opmerking: string | null
}

// ─── Analyse (uitkomst van de pure regels in @streep/utils) ─────────────────

/** Verwijzing naar de bron van een conclusie ("Waarom staat dit hier?"). */
export interface Herleiding {
  soort: 'REGEL' | 'EIS' | 'WERKGEVER' | 'BEOORDELING' | 'ARBEIDSVOORWAARDE' | 'REFERENTIE' | 'VERIFICATIE'
  /** Id van de bronrij (regel-id, eis-id, werkgever-id, ...). */
  id: string
  /** Korte omschrijving van de bron (criterium, beoordelaar, bron, zekerheid). */
  omschrijving: string
}

export interface AnalysePunt {
  tekst: string
  herleiding: Herleiding[]
}

export interface AnalyseRisico extends AnalysePunt {
  niveau: 'LAAG' | 'AANDACHTSPUNT' | 'RELEVANT'
}

export interface ConsistentieMelding {
  code:
    | 'TECHNISCH_STERK_MAAR_ONVOLDOENDE'
    | 'JOBHOPPER_BIJ_STABIELE_HISTORIE'
    | 'GROTE_AFWIJKING_BEOORDELAARS'
    | 'BUITEN_SALARISBAND'
    | 'MUST_HAVE_NIET_VOLDAAN'
  tekst: string
  /** Blokkeert het besluit tot de onderbouwing/bespreking er is. */
  blokkeert: boolean
  /** Is de vereiste onderbouwing/bespreking al vastgelegd? */
  opgelost: boolean
  herleiding: Herleiding[]
}

export interface PatroonSignalen {
  aantalWerkgevers: number
  gemiddeldeDuurMaanden: number | null
  kortsteDuurMaanden: number | null
  langsteDuurMaanden: number | null
  werkgeversLaatste5Jaar: number
  werkgeversLaatste10Jaar: number
  vertrekredenen: Array<{ reden: VertrekReden; aantal: number }>
  /** Feitelijke samenvatting ("3 vertrekken wegens ontwikkeling"), geen oordeel. */
  samenvatting: string
}

export interface ReviewVoortgang {
  /** Afgeronde second-review-reacties per rol (verschillende personen). */
  rollenKlaar: BeoordelaarRol[]
  aantalKlaar: number
  /** 2 normaal; 1 (+ directie) bij de geauditeerde uitzondering. */
  vereist: number
  compleet: boolean
  openInfoVerzoeken: number
  onbesprokenAfwijkingen: number
  /** Kan het besluit worden genomen? */
  besluitMogelijk: boolean
  blokkades: string[]
}

export interface SystemAdviesDTO {
  keuze: KandidaatAdviesKeuze
  zekerheid: 'HOOG' | 'MIDDEL' | 'LAAG'
  argumentenVoor: AnalysePunt[]
  argumentenTegen: AnalysePunt[]
  ontbrekend: AnalysePunt[]
  regel: string
}

export interface KandidaatAnalyseDTO {
  scorePerGroep: Record<BeoordelingWegingsgroep, number | null>
  technischeScore: number | null
  totaalScore: number | null
  sterkePunten: AnalysePunt[]
  aandachtspunten: AnalysePunt[]
  risicos: AnalyseRisico[]
  ontbrekend: AnalysePunt[]
  systeemAdvies: SystemAdviesDTO
  consistentie: ConsistentieMelding[]
  patroon: PatroonSignalen
  review: ReviewVoortgang
  /** Groepen waarin beoordelaars >= 1,5 punt verschillen. */
  groteAfwijkingen: Array<{ groep: BeoordelingWegingsgroep; min: number; max: number }>
}

export interface KandidaatDossierRechtenDTO {
  /** Rollen waarin de kijker in deze kandidaat mag beoordelen. */
  rollen: BeoordelaarRol[]
  magBewerken: boolean
  magBesluiten: boolean
  /** Goedkeurrecht op recruitment (o.a. de review-uitzondering). */
  magGoedkeuren: boolean
}

export interface KandidaatDossierDTO {
  kandidaatId: string
  status: KandidaatStatus
  recruiterId: string | null
  recruiterNaam: string | null
  recruiterDeadline: string | null
  gesprekGehadOp: string | null
  motivatie: string | null
  huidigeWerkgever: string | null
  opzegtermijn: string | null
  salarisWens: string | null
  reviewUitzondering: boolean
  reviewUitzonderingReden: string | null
  interneAfwijsOnderbouwing: string | null
  vacature: VacatureDTO | null
  eisToetsingen: EisToetsingDTO[]
  criteria: CriteriumDTO[]
  /** Vakgebieden (Skill) in het technische profiel, met naam. */
  vakgebieden: Array<{ skillId: string; naam: string }>
  /**
   * batch-70 (FO-530 §D8) — zonder gekoppelde vacature: voorgestelde vakgebieden
   * (vacatures met dezelfde functie, anders de meest gevraagde uit open vacatures),
   * zodat de technische sectie nooit leeg is. Met vacature: leeg.
   */
  vakgebiedVoorstel: Array<{ skillId: string; naam: string }>
  /** batch-70 (FO-530 §C4) — het oude vrije certificatenveld (read-only legacy). */
  legacyCertificaten: string | null
  beoordelingen: BeoordelingDTO[]
  werkgevers: WerkgeverDTO[]
  opleidingen: KandidaatOpleidingDTO[]
  talen: KandidaatTaalDTO[]
  arbeidsvoorwaarden: ArbeidsvoorwaardeDTO[]
  adviezen: AdviesDTO[]
  infoVerzoeken: InfoVerzoekDTO[]
  verificaties: VerificatieDTO[]
  referenties: ReferentieDTO[]
  besprekingen: BesprekingDTO[]
  gesprekWijzigingen: GesprekWijzigingDTO[]
  salarisIndicatie: SalarisIndicatieDTO | null
  /** Eerdere berekeningen (herberekening alleen op verzoek, beide zichtbaar - §5.5). */
  salarisIndicatieHistorie: SalarisIndicatieDTO[]
  /** Functiegroepen van de actieve functiematrix (keuzelijst salarisindicatie). */
  functiegroepen: string[]
  analyse: KandidaatAnalyseDTO
  tijdlijn: TijdlijnStapDTO[]
  rechten: KandidaatDossierRechtenDTO
}

/** AI-voorstel uit het CV (§D6): altijd ter bevestiging, nooit direct opgeslagen. */
export interface CvVoorstelDTO {
  werkgevers: WerkgeverInput[]
  opleidingen: KandidaatOpleidingInput[]
  talen: KandidaatTaalInput[]
}

/**
 * Een geüpload CV van de sollicitant (Document scope KANDIDAAT, bijlageType CV).
 * Bouwstenen, geen download-pad: de backoffice bouwt zelf de same-origin-proxy-link.
 */
export interface KandidaatCvDTO {
  documentId: string
  bestandsnaam: string
  mimeType: string
  grootte: number
  geuploadOp: string
  geuploadDoor: string | null
}

/** Antwoord op een CV-upload. */
export interface KandidaatCvUploadDTO {
  cv: KandidaatCvDTO
  /** De tekst is uit dit CV gelezen (zoeken en "Voorstel uit CV"); alleen bij een leesbare PDF. */
  tekstGelezen: boolean
}

/** Toegestane CV-bestanden (server en upload-veld lezen dezelfde lijst). */
export const KANDIDAAT_CV_ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
